import React, {useState} from "react";
import {DragDropContext, Droppable, Draggable} from "@hello-pangea/dnd";
import {Input} from "./input";
import {Button} from "./Button";

export default function VariantsList({variants, setVariants, errors}) {
    const [expanded, setExpanded] = useState([]);
    const [editingKeys, setEditingKeys] = useState({});

    const toggleExpand = (index) => {
        setExpanded((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    const handleChange = (index, field, value) => {
        const updated = [...variants];
        const v = updated[index];

        if (field.startsWith("attributes.")) {
            const key = field.split(".")[1];
            updated[index].attributes = {...(v.attributes || {}), [key]: value};
            if (key === "size" || key === "color" || key === "material") {
                updated[index][key] = value;
            }

        } else if (field.startsWith("attrKey.")) {
            const oldKey = field.split(".")[1];
            const newKey = value;

            if (!newKey.trim()) return;

            const newAttrs = {...(v.attributes || {})};
            newAttrs[newKey] = newAttrs[oldKey];
            delete newAttrs[oldKey];

            updated[index].attributes = newAttrs;

        } else if (field.startsWith("image.")) {
            const iIndex = parseInt(field.split(".")[1], 10);
            if (!v.image) v.image = [];
            updated[index].image[iIndex] = value;

        } else if (field === "addImage") {
            updated[index].image = [...(v.image || []), ""];

        } else if (field === "removeImage") {
            updated[index].image = (v.image || []).filter((_, i) => i !== value);

        } else {
            updated[index][field] = value;
        }

        setVariants(updated);
    };

    const handleRemoveVariant = (index) => {
        setVariants(variants.filter((_, i) => i !== index));
    };

    const handleAddEmptyAttribute = (index) => {
        const updated = [...variants];
        const attrs = {...(updated[index].attributes || {})};
        let newKey = "attribute";
        let counter = 1;
        while (attrs[newKey + counter]) counter++;
        newKey = newKey + counter;

        attrs[newKey] = "";
        updated[index].attributes = attrs;

        setVariants(updated);
    };

    const handleRemoveAttribute = (variantIndex, key) => {
        const updated = [...variants];
        const attrs = {...(updated[variantIndex].attributes || {})};
        delete attrs[key];
        updated[variantIndex].attributes = attrs;

        if (key === "size" || key === "color") {
            updated[variantIndex][key] = undefined;
        }

        setVariants(updated);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const reordered = [...variants];
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setVariants(reordered);
    };

    return (
        <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">Variants</h3>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="variants">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {variants.map((variant, index) => (
                                <Draggable key={index} draggableId={`variant-${index}`} index={index}>
                                    {(provided) => (
                                        <div
                                            className="border rounded p-3 mb-3 bg-gray-50"
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="font-semibold">
                                                    Variant #{index + 1}
                                                    {variant?.attributes?.size && ` – ${variant.attributes.size}`}
                                                    {variant?.attributes?.color && ` – ${variant.attributes.color}`}
                                                    {variant?.title && ` – title:${variant.title}`}
                                                    {variant?.price && ` - ${variant.price}`}
                                                    {variant?.quantity && ` - ${variant.quantity}`}
                                                </div>

                                                <div className="flex gap-2">
                                                    <Button variant="outline" onClick={() => toggleExpand(index)}>
                                                        {expanded.includes(index) ? "Hide" : "Edit"}
                                                    </Button>
                                                    <Button variant="outline"
                                                            onClick={() => handleRemoveVariant(index)}>
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>

                                            {expanded.includes(index) && (
                                                <div className="mt-3 space-y-3">
                                                    <Input
                                                        placeholder="Title"
                                                        value={variant.title || ""}
                                                        onChange={(e) =>
                                                            handleChange(index, "title", e.target.value)
                                                        }
                                                    />
                                                    {errors?.[index]?.title && (
                                                        <p className="text-red-600 text-sm">{errors[index].title}</p>
                                                    )}
                                                    <Input
                                                        placeholder="SKU"
                                                        value={variant.sku || ""}
                                                        onChange={(e) =>
                                                            handleChange(index, "sku", e.target.value)
                                                        }
                                                    />
                                                    {errors?.[index]?.sku && (
                                                        <p className="text-red-600 text-sm">{errors[index].sku}</p>
                                                    )}

                                                    <Input
                                                        placeholder="Price"
                                                        value={variant.price || ""}
                                                        onChange={(e) =>
                                                            handleChange(index, "price", e.target.value)
                                                        }
                                                    />

                                                    <Input
                                                        placeholder="Quantity"
                                                        value={variant.quantity || ""}
                                                        onChange={(e) =>
                                                            handleChange(index, "quantity", e.target.value)
                                                        }
                                                    />

                                                    <div>
                                                        <div className="font-semibold mb-1">Images</div>
                                                        {(variant.image || []).map((img, i) => (
                                                            <div key={i} className="flex gap-2 mb-2">
                                                                <Input
                                                                    value={img}
                                                                    placeholder="Image URL"
                                                                    onChange={(e) =>
                                                                        handleChange(index, `image.${i}`, e.target.value)
                                                                    }
                                                                    className="w-full"
                                                                />
                                                                <Button
                                                                    variant="outline"
                                                                    onClick={() =>
                                                                        handleChange(index, "removeImage", i)
                                                                    }
                                                                >
                                                                    X
                                                                </Button>
                                                            </div>
                                                        ))}
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => handleChange(index, "addImage")}
                                                        >
                                                            + Add Image
                                                        </Button>
                                                    </div>

                                                    <div>
                                                        <div className="font-semibold mb-1">Attributes</div>

                                                        {variant.attributes &&
                                                            Object.entries(variant.attributes).map(([key, value]) => {
                                                                const pendingKey =
                                                                    editingKeys[`${index}-${key}`] !== undefined
                                                                        ? editingKeys[`${index}-${key}`]
                                                                        : key;

                                                                return (
                                                                    <div key={key} className="flex gap-2 mb-2">

                                                                        <Input
                                                                            value={pendingKey}
                                                                            className="w-1/3"
                                                                            onChange={(e) => {
                                                                                setEditingKeys((prev) => ({
                                                                                    ...prev,
                                                                                    [`${index}-${key}`]: e.target.value
                                                                                }));
                                                                            }}
                                                                            onBlur={() => {
                                                                                const newKey = pendingKey.trim();

                                                                                setEditingKeys((prev) => {
                                                                                    const copy = {...prev};
                                                                                    delete copy[`${index}-${key}`];
                                                                                    return copy;
                                                                                });

                                                                                if (!newKey || newKey === key) return;

                                                                                const updated = [...variants];
                                                                                const attrs = {...(updated[index].attributes || {})};

                                                                                attrs[newKey] = attrs[key];
                                                                                delete attrs[key];

                                                                                updated[index].attributes = attrs;
                                                                                setVariants(updated);
                                                                            }}
                                                                        />

                                                                        <Input
                                                                            value={value}
                                                                            placeholder="Value"
                                                                            onChange={(e) =>
                                                                                handleChange(index, `attributes.${key}`, e.target.value)
                                                                            }
                                                                            className="w-2/3"
                                                                        />

                                                                        <Button
                                                                            variant="outline"
                                                                            onClick={() => handleRemoveAttribute(index, key)}
                                                                        >
                                                                            X
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            })}

                                                        <Button
                                                            variant="outline"
                                                            onClick={() => handleAddEmptyAttribute(index)}
                                                        >
                                                            + Add Attribute
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <Button
                variant="outline"
                onClick={() =>
                    setVariants([
                        ...variants,
                        {
                            sku: "",
                            price: "",
                            quantity: "",
                            image: [],
                            attributes: {}
                        }
                    ])
                }
            >
                + Add Variant
            </Button>
        </div>
    );
}
